import React, { useState, useEffect, useCallback } from 'react';
import { reportsApi } from '../api';
import { useActivity } from '../App';
import toast from 'react-hot-toast';

// ─── CSV Download helper ───────────────────────────────────────────────────────
const downloadCSV = (data, filename) => {
  if (!data || data.length === 0) { toast.error('No data to export'); return; }
  const headers = Object.keys(data[0]);
  const rows    = data.map(row => headers.map(h => `"${row[h] ?? ''}"`).join(','));
  const csv     = [headers.join(','), ...rows].join('\n');
  const blob    = new Blob([csv], { type: 'text/csv' });
  const url     = URL.createObjectURL(blob);
  const a       = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

// ─── Shared styles ─────────────────────────────────────────────────────────────
const cardS = { background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '12px', padding: '20px' };
const thS   = { padding: '10px 14px', textAlign: 'left', fontSize: '0.74rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap', background: 'var(--bg-tertiary)' };
const tdS   = { padding: '11px 14px', fontSize: '0.85rem', borderBottom: '1px solid var(--border-color)' };
const tabBtnS = (active) => ({ padding: '8px 18px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, background: active ? 'var(--accent-primary)' : 'var(--bg-tertiary)', color: active ? 'white' : 'var(--text-secondary)', transition: 'all 0.2s' });
const exportBtnS = { padding: '8px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, background: 'rgba(34,197,94,0.15)', color: '#22c55e', display: 'flex', alignItems: 'center', gap: '6px' };
const spinS = { display: 'inline-block', width: '18px', height: '18px', border: '2px solid var(--border-color)', borderTopColor: 'var(--accent-primary)', borderRadius: '50%', animation: 'spin 0.7s linear infinite' };

const STATUS_BADGE = {
  ACTIVE:   { bg: 'rgba(34,197,94,0.15)',  color: '#22c55e' },
  INACTIVE: { bg: 'rgba(245,158,11,0.15)', color: '#f59e0b' },
  BANNED:   { bg: 'rgba(239,68,68,0.15)',  color: '#ef4444' },
};

// ─── Reports Page ──────────────────────────────────────────────────────────────
const Reports = () => {
  const { addLog } = useActivity();

  const [tab,        setTab]        = useState('users');
  const [users,      setUsers]      = useState([]);
  const [battles,    setBattles]    = useState([]);
  const [withdrawals,setWithdrawals]= useState([]);
  const [loading,    setLoading]    = useState(false);
  const [search,     setSearch]     = useState('');
  const [page,       setPage]       = useState(0);
  const PAGE_SIZE = 15;

  // ── Fetch users report ──────────────────────────────────────────────────────
  const fetchUsers = useCallback(() => {
    setLoading(true);
    reportsApi.getUsers()
      .then(res => setUsers(res.data?.data || []))
      .catch(err => toast.error(err.response?.data?.message || 'Failed to load user report'))
      .finally(() => setLoading(false));
  }, []);

  // ── Fetch battles report ────────────────────────────────────────────────────
  const fetchBattles = useCallback(() => {
    setLoading(true);
    reportsApi.getBattles()
      .then(res => setBattles(res.data?.data || []))
      .catch(err => toast.error(err.response?.data?.message || 'Failed to load battle report'))
      .finally(() => setLoading(false));
  }, []);

  // ── Fetch withdrawals report ────────────────────────────────────────────────
  const fetchWithdrawals = useCallback(() => {
    setLoading(true);
    reportsApi.getWithdrawals()
      .then(res => setWithdrawals(res.data?.data || []))
      .catch(err => toast.error(err.response?.data?.message || 'Failed to load withdrawal report'))
      .finally(() => setLoading(false));
  }, []);

  // ── Load data on tab change ─────────────────────────────────────────────────
  useEffect(() => {
    setSearch(''); setPage(0);
    if (tab === 'users')       fetchUsers();
    else if (tab === 'battles')    fetchBattles();
    else if (tab === 'withdrawals') fetchWithdrawals();
  }, [tab]);

  // ── Export CSV ──────────────────────────────────────────────────────────────
  const handleExport = () => {
    if (tab === 'users') {
      downloadCSV(users, 'walkbattle_users.csv');
      addLog('Report Exported', 'Users CSV', 'export');
    } else if (tab === 'battles') {
      downloadCSV(battles, 'walkbattle_battles.csv');
      addLog('Report Exported', 'Battles CSV', 'export');
    } else {
      downloadCSV(withdrawals, 'walkbattle_withdrawals.csv');
      addLog('Report Exported', 'Withdrawals CSV', 'export');
    }
    toast.success('CSV downloaded!');
  };

  // ── Filter & paginate ───────────────────────────────────────────────────────
  const currentData =
    tab === 'users'       ? users :
    tab === 'battles'     ? battles :
    withdrawals;

  const filteredData = currentData.filter(row => {
    if (!search) return true;
    const q = search.toLowerCase();
    return Object.values(row).some(v => String(v ?? '').toLowerCase().includes(q));
  });

  const totalPages = Math.ceil(filteredData.length / PAGE_SIZE);
  const paged      = filteredData.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  // ── KPI counts ──────────────────────────────────────────────────────────────
  const activeUsers   = users.filter(u => u.status === 'ACTIVE').length;
  const totalBattles  = battles.length;
  const pendingWd     = withdrawals.filter(w => w.status === 'PENDING').length;
  const totalWdAmt    = withdrawals.reduce((s, w) => s + (w.amount || 0), 0);

  return (
    <div className="animate-in">
      {/* Header */}
      <div className="page-header">
        <div className="page-header-left">
          <div className="breadcrumb"><span>System</span> / <span className="current">Reports</span></div>
          <h1>Reports & Analytics</h1>
          <p style={{ color: 'var(--text-secondary)' }}>Live data from backend — download as CSV</p>
        </div>
        <button style={exportBtnS} onClick={handleExport}>
          📥 Export CSV
        </button>
      </div>

      {/* KPI cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '14px', marginBottom: '24px' }}>
        <div style={{ ...cardS, borderTop: '3px solid #6366f1' }}>
          <div style={{ fontSize: '0.73rem', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '6px' }}>Total App Users</div>
          <div style={{ fontSize: '1.8rem', fontWeight: 800 }}>{users.length}</div>
        </div>
        <div style={{ ...cardS, borderTop: '3px solid #22c55e' }}>
          <div style={{ fontSize: '0.73rem', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '6px' }}>Active Users</div>
          <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#22c55e' }}>{activeUsers}</div>
        </div>
        <div style={{ ...cardS, borderTop: '3px solid #8b5cf6' }}>
          <div style={{ fontSize: '0.73rem', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '6px' }}>Total Battles</div>
          <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#8b5cf6' }}>{totalBattles}</div>
        </div>
        <div style={{ ...cardS, borderTop: '3px solid #ef4444' }}>
          <div style={{ fontSize: '0.73rem', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '6px' }}>Pending Withdrawals</div>
          <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#ef4444' }}>{pendingWd}</div>
        </div>
        <div style={{ ...cardS, borderTop: '3px solid #f59e0b' }}>
          <div style={{ fontSize: '0.73rem', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '6px' }}>Total Withdrawal ₹</div>
          <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#f59e0b' }}>₹{totalWdAmt.toLocaleString()}</div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
        {[
          { key: 'users',        label: '👥 Users' },
          { key: 'battles',      label: '⚔️ Battles' },
          { key: 'withdrawals',  label: '💸 Withdrawals' },
        ].map(t => (
          <button key={t.key} style={tabBtnS(tab === t.key)} onClick={() => setTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Search + export bar */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <input
          style={{ padding: '8px 14px', background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: '0.85rem', outline: 'none', flex: 1, minWidth: '200px' }}
          placeholder={`🔍 Search ${tab}...`}
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(0); }}
        />
        <span style={{ padding: '8px 14px', background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', borderRadius: '8px', fontSize: '0.82rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
          {filteredData.length.toLocaleString()} records
        </span>
      </div>

      {/* Table */}
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '12px', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-muted)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                <span style={spinS} /> Loading...
              </div>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                {/* ── Users columns ────────────────────── */}
                {tab === 'users' && (
                  <tr>
                    {['#','Name','Email','Mobile','City','Status','Type','Level','XP','Total KM','Joined'].map(h => (
                      <th key={h} style={thS}>{h}</th>
                    ))}
                  </tr>
                )}
                {/* ── Battles columns ───────────────────── */}
                {tab === 'battles' && (
                  <tr>
                    {['#','Name','Status','Entry','Fee (₹)','Venue','Start Time','End Time'].map(h => (
                      <th key={h} style={thS}>{h}</th>
                    ))}
                  </tr>
                )}
                {/* ── Withdrawals columns ───────────────── */}
                {tab === 'withdrawals' && (
                  <tr>
                    {['#','User ID','Amount (₹)','Method','Handle','Status','Requested At'].map(h => (
                      <th key={h} style={thS}>{h}</th>
                    ))}
                  </tr>
                )}
              </thead>
              <tbody>
                {paged.length === 0 ? (
                  <tr><td colSpan={12} style={{ textAlign: 'center', padding: '48px', color: 'var(--text-muted)' }}>No data found</td></tr>
                ) : paged.map((row, i) => (
                  <tr key={row.id || i}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>

                    {tab === 'users' && <>
                      <td style={tdS}>{page * PAGE_SIZE + i + 1}</td>
                      <td style={tdS}><span style={{ fontWeight: 600 }}>{row.name}</span></td>
                      <td style={tdS}><span style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>{row.email}</span></td>
                      <td style={tdS}>{row.mobile || '—'}</td>
                      <td style={tdS}>{row.city || '—'}</td>
                      <td style={tdS}>
                        <span style={{ ...STATUS_BADGE[row.status], padding: '2px 8px', borderRadius: '20px', fontSize: '0.73rem', fontWeight: 600 }}>
                          {row.status}
                        </span>
                      </td>
                      <td style={tdS}>{row.userType}</td>
                      <td style={tdS}>{row.level}</td>
                      <td style={tdS}><span style={{ color: '#f59e0b' }}>{row.xpPoints?.toLocaleString()}</span></td>
                      <td style={tdS}>{row.totalKm} km</td>
                      <td style={tdS}><span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{row.joinedAt ? new Date(row.joinedAt).toLocaleDateString('en-IN') : '—'}</span></td>
                    </>}

                    {tab === 'battles' && <>
                      <td style={tdS}>{page * PAGE_SIZE + i + 1}</td>
                      <td style={tdS}><span style={{ fontWeight: 600 }}>{row.name}</span></td>
                      <td style={tdS}>
                        <span style={{ padding: '2px 8px', borderRadius: '20px', fontSize: '0.73rem', fontWeight: 600, background: row.status === 'ACTIVE' ? 'rgba(34,197,94,0.15)' : row.status === 'UPCOMING' ? 'rgba(99,102,241,0.15)' : 'rgba(100,116,139,0.15)', color: row.status === 'ACTIVE' ? '#22c55e' : row.status === 'UPCOMING' ? '#6366f1' : '#94a3b8' }}>
                          {row.status}
                        </span>
                      </td>
                      <td style={tdS}>{row.entryType}</td>
                      <td style={tdS}>{row.entryFee ? `₹${row.entryFee}` : 'FREE'}</td>
                      <td style={tdS}>{row.venue || '—'}</td>
                      <td style={tdS}><span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{row.startTime ? new Date(row.startTime).toLocaleString('en-IN') : '—'}</span></td>
                      <td style={tdS}><span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{row.endTime   ? new Date(row.endTime).toLocaleString('en-IN')   : '—'}</span></td>
                    </>}

                    {tab === 'withdrawals' && <>
                      <td style={tdS}>{page * PAGE_SIZE + i + 1}</td>
                      <td style={tdS}>{row.userId}</td>
                      <td style={tdS}><span style={{ fontWeight: 700, color: '#22c55e' }}>₹{row.amount?.toLocaleString()}</span></td>
                      <td style={tdS}>{row.method}</td>
                      <td style={tdS}><code style={{ fontSize: '0.78rem', background: 'var(--bg-tertiary)', padding: '2px 6px', borderRadius: '4px' }}>{row.paymentHandle}</code></td>
                      <td style={tdS}>
                        <span style={{ padding: '2px 8px', borderRadius: '20px', fontSize: '0.73rem', fontWeight: 600, background: row.status === 'COMPLETED' ? 'rgba(34,197,94,0.15)' : row.status === 'PENDING' ? 'rgba(245,158,11,0.15)' : 'rgba(239,68,68,0.15)', color: row.status === 'COMPLETED' ? '#22c55e' : row.status === 'PENDING' ? '#f59e0b' : '#ef4444' }}>
                          {row.status}
                        </span>
                      </td>
                      <td style={tdS}><span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{row.requestedAt ? new Date(row.requestedAt).toLocaleDateString('en-IN') : '—'}</span></td>
                    </>}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderTop: '1px solid var(--border-color)' }}>
            <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
              {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, filteredData.length)} of {filteredData.length}
            </span>
            <div style={{ display: 'flex', gap: '6px' }}>
              <button disabled={page === 0} onClick={() => setPage(p => p - 1)}
                style={{ padding: '5px 12px', borderRadius: '6px', border: '1px solid var(--border-color)', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', cursor: 'pointer', opacity: page === 0 ? 0.4 : 1 }}>‹</button>
              <span style={{ padding: '5px 12px', background: 'var(--accent-primary)', color: 'white', borderRadius: '6px', fontSize: '0.82rem', fontWeight: 600 }}>
                {page + 1} / {totalPages}
              </span>
              <button disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}
                style={{ padding: '5px 12px', borderRadius: '6px', border: '1px solid var(--border-color)', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', cursor: 'pointer', opacity: page >= totalPages - 1 ? 0.4 : 1 }}>›</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Reports;
